import { AxiosError } from 'axios';
import ActionError from './ActionError';

// Error types for better categorization
export enum ErrorType {
  NETWORK = 'NETWORK',
  SERVER = 'SERVER',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  VALIDATION = 'VALIDATION',
  UNKNOWN = 'UNKNOWN',
}

export interface SerializableError {
  message: string;
  type: ErrorType;
  statusCode?: number;
  errorCode?: string;
  isNetworkError?: boolean;
}

/**
 * Comprehensive helper to determine if an error is a network connectivity failure.
 * This is the single source of truth for network error detection across the app.
 *
 * Detects:
 * - Connection refused (ECONNREFUSED)
 * - DNS lookup failures (ENOTFOUND)
 * - Connection reset (ECONNRESET)
 * - Timeouts (ETIMEDOUT)
 * - Browser network errors (ERR_NETWORK)
 * - Axios network errors
 * - Status code 0 (no response)
 * - ActionError with NETWORK_ERROR code
 */
export const isNetworkError = (
  error: AxiosError | Error | ActionError | unknown
): boolean => {
  if (!error) return false;

  // Handle plain objects with error properties (e.g., Redux payloads)
  if (typeof error === 'object' && !('message' in error)) {
    const obj = error as { errorCode?: string; statusCode?: number };
    return obj.errorCode === 'NETWORK_ERROR' || obj.statusCode === 0;
  }

  // For ActionError, check the errorCode first
  if (error instanceof ActionError) {
    return error.errorCode === 'NETWORK_ERROR' || error.statusCode === 0;
  }

  const errorObj = error as {
    code?: string;
    message?: string;
    response?: { status?: number };
    statusCode?: number;
    errorCode?: string;
  };

  // Check error codes (Node.js / Axios)
  if (errorObj.code) {
    const networkCodes = [
      'ECONNREFUSED',
      'ENOTFOUND',
      'ECONNRESET',
      'ETIMEDOUT',
      'ERR_NETWORK',
      'ERR_CONNECTION_REFUSED',
    ];
    if (networkCodes.includes(errorObj.code)) {
      return true;
    }
  }

  // Check for status code 0 (no response received)
  if (errorObj.statusCode === 0) {
    return true;
  }

  // Check errorCode property
  if (errorObj.errorCode === 'NETWORK_ERROR') {
    return true;
  }

  // Check error message for network-related strings
  if (errorObj.message) {
    const message = errorObj.message.toLowerCase();
    const networkPhrases = [
      'network error',
      'err_network',
      'err_connection_refused',
      'econnrefused',
      'unable to connect',
      'failed to fetch',
      'server is unreachable',
      'connection refused',
    ];
    if (networkPhrases.some((phrase) => message.includes(phrase))) {
      return true;
    }
  }

  return false;
};

// Transform any error into a user-friendly, serializable format
export const transformError = (error: unknown): SerializableError => {
  if (error instanceof ActionError) {
    const isNetwork =
      error.errorCode === 'NETWORK_ERROR' || isNetworkError(error);
    return {
      message: isNetwork
        ? 'Unable to connect to server. Please check your internet connection and try again.'
        : error.message,
      type: isNetwork ? ErrorType.NETWORK : categorizeError(error.statusCode),
      statusCode: error.statusCode,
      errorCode: error.errorCode,
      isNetworkError: isNetwork,
    };
  }

  if (error instanceof Error && 'response' in error) {
    const axiosError = error as AxiosError;
    const isNetwork = isNetworkError(axiosError);

    if (isNetwork) {
      return {
        message:
          'Unable to connect to server. Please check your internet connection and try again.',
        type: ErrorType.NETWORK,
        statusCode: 0,
        errorCode: 'NETWORK_ERROR',
        isNetworkError: true,
      };
    }

    return {
      message:
        (axiosError.response?.data as { message?: string })?.message ||
        axiosError.message,
      type: categorizeError(axiosError.response?.status),
      statusCode: axiosError.response?.status,
      isNetworkError: false,
    };
  }

  if (error instanceof Error) {
    const isNetwork = isNetworkError(error);
    return {
      message: isNetwork
        ? 'Unable to connect to server. Please check your internet connection and try again.'
        : error.message,
      type: isNetwork ? ErrorType.NETWORK : ErrorType.UNKNOWN,
      statusCode: isNetwork ? 0 : undefined,
      errorCode: isNetwork ? 'NETWORK_ERROR' : undefined,
      isNetworkError: isNetwork,
    };
  }

  return {
    message: 'An unexpected error occurred. Please try again.',
    type: ErrorType.UNKNOWN,
    isNetworkError: false,
  };
};

// Categorize errors by status code
const categorizeError = (statusCode?: number): ErrorType => {
  if (!statusCode) return ErrorType.UNKNOWN;

  if (statusCode === 401) return ErrorType.AUTHENTICATION;
  if (statusCode === 403) return ErrorType.AUTHORIZATION;
  if (statusCode >= 400 && statusCode < 500) return ErrorType.VALIDATION;
  if (statusCode >= 500) return ErrorType.SERVER;

  return ErrorType.UNKNOWN;
};

// Get user-friendly error message based on error type
export const getErrorMessage = (error: SerializableError): string => {
  switch (error.type) {
    case ErrorType.NETWORK:
      return 'Unable to connect to server. Please check your internet connection and try again.';
    case ErrorType.AUTHENTICATION:
      return 'Authentication required. Please log in again.';
    case ErrorType.AUTHORIZATION:
      return 'You do not have permission to perform this action.';
    case ErrorType.SERVER:
      return 'Server error occurred. Please try again later.';
    case ErrorType.VALIDATION:
      return error.message || 'Invalid request. Please check your input.';
    default:
      return error.message || 'An unexpected error occurred. Please try again.';
  }
};

// Check if error indicates server is unavailable
export const isServerUnavailable = (error: SerializableError): boolean => {
  return error.type === ErrorType.NETWORK || error.statusCode === 0;
};

/**
 * Safely converts any error to a JSON-serializable format for Redux rejectWithValue.
 * This handles ActionError, AxiosError, Error, and any other error types.
 */
export const serializeError = (error: unknown): Record<string, unknown> => {
  // If error is already a plain serialized object (not an Error instance), return it
  if (
    error &&
    typeof error === 'object' &&
    !(error instanceof Error) &&
    ('errorCode' in error || 'statusCode' in error || 'message' in error)
  ) {
    return error as Record<string, unknown>;
  }

  // If error has toJSON method, use it
  if (
    error &&
    typeof error === 'object' &&
    'toJSON' in error &&
    typeof error.toJSON === 'function'
  ) {
    return error.toJSON();
  }

  // If error is ActionError instance (should have toJSON, but double-check)
  if (error instanceof ActionError) {
    return {
      name: error.name,
      message: error.message,
      statusCode: error.statusCode,
      errorCode: error.errorCode,
      stack: error.stack,
    };
  }

  // If error is AxiosError
  if (error instanceof Error && 'response' in error) {
    const axiosError = error as AxiosError;
    return {
      name: 'AxiosError',
      message:
        (axiosError.response?.data as { message?: string })?.message ||
        axiosError.message,
      statusCode: axiosError.response?.status,
      stack: axiosError.stack,
    };
  }

  // If error is standard Error
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }

  // If error is string
  if (typeof error === 'string') {
    return {
      name: 'Error',
      message: error,
    };
  }

  // Fallback for unknown error types
  return {
    name: 'UnknownError',
    message: 'An unexpected error occurred',
    originalError: JSON.stringify(error),
  };
};
